import React, { useState } from 'react';
import {
  Box, Typography, Paper, Stack, Button, Chip, Table,
  TableBody, TableCell, TableHead, TableRow, Tooltip,
  TextField, InputAdornment, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import WorkIcon from '@mui/icons-material/Work';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { BRAND } from '../theme/tokens';
import { StatusChip } from '../components/StatusChip';

const FI_ORANGE = '#F57C00';

// Stable avatar colour per customer initial — purely cosmetic.
const AVATAR_COLORS = ['#2e7d32', '#1565c0', '#b71c1c', '#e65100', '#6a1b9a', '#00838f'];
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

export const InvoiceListPage: React.FC = () => {
  const navigate = useNavigate();
  const { invoices, sites, createInvoice } = useData();
  const [search, setSearch] = useState('');

  // New Invoice dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState('');
  const [newTemplate, setNewTemplate] = useState('Original');

  const handleOpenDialog = () => {
    setNewCustomer('');
    setNewTemplate('Original');
    setDialogOpen(true);
  };

  const handleCreate = () => {
    const id = createInvoice({ customerId: newCustomer || undefined, template: newTemplate });
    setDialogOpen(false);
    navigate(`/invoices/${id}`);
  };

  const customerName = (inv: typeof invoices[number]) =>
    inv.customerName ?? sites.find(s => s.id === inv.customerId)?.businessName ?? '—';

  const filtered = invoices.filter(inv => {
    const term = search.toLowerCase();
    return (
      inv.id.toLowerCase().includes(term) ||
      (inv.title ?? '').toLowerCase().includes(term) ||
      customerName(inv).toLowerCase().includes(term) ||
      inv.status.toLowerCase().includes(term)
    );
  });

  const totalValue = invoices.reduce((s, inv) => s + inv.total, 0);
  const outstanding = invoices.filter(inv => ['Draft', 'Awaiting Approval', 'Approved', 'Sent', 'Partially Paid'].includes(inv.status)).length;
  const paid = invoices.filter(inv => inv.status === 'Paid').length;
  const owing = invoices.reduce((s, inv) => s + inv.owingAmount, 0);

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} sx={{ color: BRAND }}>Invoices</Typography>
          <Typography variant="body2" color="text.secondary">Northbridge Diesel &amp; Mechanical</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          size="small"
          onClick={handleOpenDialog}
          sx={{ backgroundColor: FI_ORANGE, textTransform: 'none', fontWeight: 700 }}
        >
          New Invoice
        </Button>
      </Stack>

      {/* Summary cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
        {[
          { label: 'Total Invoiced', value: `$${totalValue.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`, color: BRAND, bg: '#f5f0ff' },
          { label: 'Outstanding', value: outstanding, color: '#e65100', bg: '#fff3e0' },
          { label: 'Paid', value: paid, color: '#1b5e20', bg: '#e8f5e9' },
          { label: 'Amount Owing', value: `$${owing.toLocaleString('en-AU', { minimumFractionDigits: 2 })}`, color: '#b71c1c', bg: '#ffebee' },
        ].map(card => (
          <Paper key={card.label} sx={{ flex: 1, p: 1.5, border: `1px solid ${card.color}30`, backgroundColor: card.bg }}>
            <Typography variant="h5" fontWeight={800} sx={{ color: card.color }}>{card.value}</Typography>
            <Typography variant="caption" sx={{ color: card.color, fontWeight: 600 }}>{card.label}</Typography>
          </Paper>
        ))}
      </Stack>

      {/* Search */}
      <Box mb={2}>
        <TextField size="small" placeholder="Search invoices, customers, status..."
          value={search} onChange={e => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ width: 340 }} />
      </Box>

      {/* Table */}
      <Paper sx={{ border: '1px solid #e0e0e0', overflow: 'hidden' }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f0ff' }}>
              {['Invoice #', 'Date', 'Customer', 'Title', 'Lines', 'Ex GST', 'GST', 'Total', 'Owing', 'Status', ''].map(h => (
                <TableCell key={h} sx={{ fontWeight: 700, fontSize: '0.72rem', color: BRAND }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map(inv => {
              const cName = customerName(inv);
              return (
                <TableRow
                  key={inv.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <TableCell>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Typography variant="caption" sx={{ color: FI_ORANGE, fontWeight: 700 }}>{inv.id}</Typography>
                      {inv.lines === 0 && (
                        <Chip label="0 items" size="small"
                          sx={{ fontSize: '0.58rem', height: 15, fontWeight: 700,
                            backgroundColor: '#fff3e0', color: FI_ORANGE, border: `1px solid ${FI_ORANGE}40` }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>
                    {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString('en-AU') : '—'}
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.8} alignItems="center">
                      <Avatar sx={{ width: 22, height: 22, fontSize: '0.6rem', fontWeight: 800,
                        backgroundColor: avatarColor(cName) }}>
                        {cName.slice(0, 2).toUpperCase()}
                      </Avatar>
                      <Typography variant="caption" sx={{ fontSize: '0.75rem', maxWidth: 170 }} noWrap>
                        {cName}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', maxWidth: 220 }}>
                    <Typography sx={{ fontSize: '0.75rem', color: inv.title ? '#333' : '#bbb' }} noWrap>
                      {inv.title || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: inv.lines === 0 ? 700 : 400, color: inv.lines === 0 ? FI_ORANGE : '#333' }}>
                      {inv.lines}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: '0.75rem' }}>${inv.exGst.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>${inv.gst.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700 }}>${inv.total.toFixed(2)}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', fontWeight: 700, color: inv.owingAmount > 0 ? '#b71c1c' : '#2e7d32' }}>
                    ${inv.owingAmount.toFixed(2)}
                  </TableCell>
                  <TableCell><StatusChip status={inv.status} dense /></TableCell>
                  <TableCell>
                    <Tooltip title="Open invoice">
                      <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 13 }} />}
                        sx={{ textTransform: 'none', fontSize: '0.68rem', borderColor: BRAND, color: BRAND }}
                        onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.id}`); }}>
                        Open
                      </Button>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Box sx={{ px: 2, py: 1, borderTop: '1px solid #f0f0f0', backgroundColor: '#fafafa' }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} invoices</Typography>
        </Box>
      </Paper>

      {/* New Invoice Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '1rem' }}>New Invoice</Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#888', mt: 0.2 }}>
            Creates a Draft invoice and opens it for editing.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 1.5 }}>
          <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, backgroundColor: '#f5f0ff', border: `1px solid ${BRAND}33` }}>
            <Typography sx={{ fontSize: '0.72rem', color: BRAND, fontWeight: 700, mb: 0.3 }}>How this works</Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#555', lineHeight: 1.5 }}>
              Pick a customer and template to start. You can add line items, set the markup,
              and adjust the invoice status (Awaiting Approval → Approved → Sent) on the next screen.
            </Typography>
          </Box>

          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Customer</InputLabel>
            <Select value={newCustomer} label="Customer" onChange={e => setNewCustomer(e.target.value)} displayEmpty>
              <MenuItem value=""><em>Unassigned</em></MenuItem>
              {sites.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.businessName ?? s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Template</InputLabel>
            <Select value={newTemplate} label="Template" onChange={e => setNewTemplate(e.target.value)}>
              {['Original', 'Service Invoice', 'Project Invoice'].map(t => (
                <MenuItem key={t} value={t}>{t}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Alert severity="info" icon={<WorkIcon fontSize="small" />} sx={{ mt: 2, fontSize: '0.72rem' }}>
            Owner / Sales Rep defaults to <strong>Paul Tyrrell</strong> — change it on the invoice.
          </Alert>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none', color: '#888' }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            sx={{ backgroundColor: BRAND, textTransform: 'none', fontWeight: 700, px: 3,
              '&:hover': { backgroundColor: '#4a3580' } }}
          >
            Create Invoice
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceListPage;
